<template>
  <div class="chat-messages" v-if="userMessageCount > 0">
    <template v-for="(msg, index) in messages">
      <!-- 隐藏掉最初的那句默认打招呼，因为已经有了顶部卡片 -->
      <div 
        v-if="!shouldHideMessage(msg, index)"
        :key="getMessageKey(msg, index)"
        :class="['message-row', msg.role]"
        :data-message-index="index"
      >
        <div class="message-wrapper"
             @mouseenter="handleMouseEnter(index)"
             @mouseleave="handleMouseLeave(index)"
             @click.stop="handleMessageClick(index)"
        >
          <div v-if="msg.role === 'assistant' && getCompanionNames(msg, index)" class="companion-message-label" aria-label="伙伴">
            <span>{{ getCompanionNames(msg, index) }}</span>
          </div>
          <div class="message-content" :class="{ 'generating-border': isSending && msg.role === 'assistant' && index === messages.length - 1 }">
            <!-- Workspace tools are shown as independent Claude-style steps, separate from reasoning. -->
            <div v-if="msg.role === 'assistant' && msg.workspaceSteps && msg.workspaceSteps.length" class="workspace-steps" aria-label="工作区操作记录">
              <details v-for="(step, stepIndex) in msg.workspaceSteps" :key="step.id" class="workspace-step" :class="`workspace-step-${step.status}`" :open="step.status === 'running'" @click.stop>
                <summary class="workspace-step-summary">
                  <span class="workspace-step-icon">
                    <svg v-if="step.status === 'running'" class="workspace-step-spinner" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8"></circle></svg>
                    <svg v-else-if="step.status === 'done'" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"></path></svg>
                    <svg v-else viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8v5"></path><path d="M12 16h.01"></path><circle cx="12" cy="12" r="9"></circle></svg>
                  </span>
                  <span class="workspace-step-body">
                    <strong><span class="workspace-step-number">{{ stepIndex + 1 }}</span>{{ step.title }}</strong>
                    <small v-if="step.status === 'running'">执行中</small>
                    <small v-else-if="step.status === 'error'">未完成</small>
                    <small v-else>已完成</small>
                    <em v-if="step.summary">{{ step.summary }}</em>
                  </span>
                  <span class="workspace-step-chevron" aria-hidden="true">⌄</span>
                </summary>
                <div class="workspace-step-detail" @click.stop>
                  <div class="workspace-step-detail-section">
                    <span>工具名称</span>
                    <code>{{ step.name }}</code>
                  </div>
                  <div v-if="step.argumentsText" class="workspace-step-detail-section">
                    <span>工具指令</span>
                    <pre>{{ step.argumentsText }}</pre>
                  </div>
                  <div v-if="step.resultText" class="workspace-step-detail-section">
                    <span>执行结果</span>
                    <pre>{{ step.resultText }}</pre>
                  </div>
                </div>
              </details>
              <!-- 浏览器框架 CSS 预览确认卡:预览已在浏览器上生效,用户确认后才落盘 -->
              <template v-for="step in msg.workspaceSteps">
                <div v-if="step.cssPreview" :key="step.id + '-css'" class="css-preview-card" :class="'css-preview-' + step.cssPreview.state" role="group" aria-label="样式预览确认">
                  <div class="css-preview-text">
                    <strong>{{ cssPreviewTitle(step.cssPreview) }}</strong>
                    <span v-if="step.cssPreview.summary">{{ step.cssPreview.summary }}</span>
                    <em v-if="step.cssPreview.error">{{ step.cssPreview.error }}</em>
                  </div>
                  <div class="css-preview-actions">
                    <template v-if="step.cssPreview.state === 'pending'">
                      <button type="button" class="css-preview-btn" :disabled="step.cssPreview.busy" @click.stop="$emit('css-preview-action', index, step.id, 'discard')">放弃</button>
                      <button type="button" class="css-preview-btn primary" :disabled="step.cssPreview.busy" @click.stop="$emit('css-preview-action', index, step.id, 'apply')">应用</button>
                    </template>
                    <button v-else-if="step.cssPreview.state === 'applied'" type="button" class="css-preview-btn" :disabled="step.cssPreview.busy" @click.stop="$emit('css-preview-action', index, step.id, 'undo')">撤销</button>
                  </div>
                </div>
              </template>
            </div>

            <!-- Thinking Process Block -->
            <div v-if="msg.thinking" class="thinking-block" :class="{ 'is-thinking': msg.isThinking && !msg.content }">
              <details :open="msg.isThinking && index === messages.length - 1">
                <summary>
                  <div class="thinking-header">
                    <span class="thinking-icon">
                      <!-- Search Icon if searching -->
                      <svg v-if="msg.isSearch" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <!-- Thinking Icon if not searching -->
                      <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"></path>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                    </span>
                    <span class="thinking-title">{{ msg.isSearch ? $t('browserAgentChat.thinkingSearch') : $t('browserAgentChat.thinking') }}</span>
                    <span class="chevron-icon">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </div>
                </summary>
                <div class="thinking-content">
                  <MarkdownRenderer :content="msg.thinking" :protect-selection="isStreamingLastMessage(msg, index)" :enhance-formulas-on-render="!isStreamingLastMessage(msg, index)" @copy-code="handleCopyCodeEvent" @preview-formula="handleFormulaPreview" @preview-image="openImage" />
                </div>
              </details>
            </div>
            
            <!-- Attachments Display -->
            <div v-if="msg.attachments && msg.attachments.length > 0" class="message-attachments">
                <div v-for="(att, i) in msg.attachments" :key="i" class="message-attachment-item">
                    <img v-if="att.type === 'image'" :src="att.url" alt="Attachment" loading="lazy" class="message-image" @click="openImage(att.url)">
                    <div v-else-if="att.type === 'file'" class="file-attachment-bubble" @click="openFile(att.url)" :title="$t('browserAgentChat.viewFile', { name: att.name })">
                         <div class="file-icon-small">
                           <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                         </div>
                         <span class="file-name">{{ att.name }}</span>
                         <div class="file-download-icon">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4"></path>
                              <polyline points="7 10 12 15 17 10"></polyline>
                              <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                         </div>
                    </div>
                </div>
            </div>
            <div v-else-if="msg.imageUrl && !(msg.generatedImageUrls && msg.generatedImageUrls.length) && !(msg.content && msg.content.indexOf(msg.imageUrl) !== -1)" class="message-attachment">
                <img :src="msg.imageUrl" alt="Attachment" loading="lazy" class="message-image message-image-inline" @click="openImage(msg.imageUrl)">
            </div>

            <div v-if="!msg.content && !msg.isGeneratingImage && msg.role === 'assistant' && (!msg.thinking || isPendingStatus(msg.chatStatus))" class="typing-indicator">
              <div v-if="msg.statusText" class="smart-search-status">
                <svg v-if="isSpinnerStatus(msg.chatStatus)" class="spinner" viewBox="0 0 50 50" width="16" height="16"><circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5" stroke="currentColor"></circle></svg>
                <span v-else-if="msg.chatStatus === 'scraping'">📄</span>
                <span v-else-if="msg.chatStatus === 'knowledge_base'">📚</span>
                <span v-else-if="msg.chatStatus === 'summarizing'">✨</span>
                <span>{{ msg.statusText }}</span>
                <div v-if="msg.kbRelevanceJudging" class="kb-judging-actions">
                  <button @click.stop="useKnowledgeBaseDirectly" class="kb-judging-btn primary">
                    {{ $t('browserAgentChat.kbUseDirectly') }}
                  </button>
                  <button @click.stop="skipKnowledgeBase" class="kb-judging-btn secondary">
                    {{ $t('browserAgentChat.kbSkip') }}
                  </button>
                </div>
                <button v-else-if="isSkippableStatus(msg.chatStatus)" @click.stop="cancelScraping" class="cancel-scrape-btn">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1" ry="1"></rect></svg>
                  <span>{{ $t('browserAgentChat.answerDirectly') }}</span>
                </button>
              </div>
              <template v-else>
                <span></span>
                <span></span>
                <span></span>
              </template>
            </div>
            
            <!-- Skeleton Loader for Image Generation -->
            <div v-if="msg.isGeneratingImage" class="image-skeleton-loader">
                <div class="skeleton-image">
                    <div class="shimmer"></div>
                    <div class="skeleton-icon">
                        <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                    </div>
                </div>
                <div class="skeleton-text">{{ $t('browserAgentChat.drawing') }}<span v-if="msg.imageTask && msg.imageTask.progress != null"> · {{ msg.imageTask.progress }}%</span></div>
            </div>

            <div v-if="msg.imageTask && !msg.isGeneratingImage" class="image-task-status" :class="`image-task-${msg.imageTask.status}`">
              <span>{{ ['failed', 'timeout'].includes(msg.imageTask.status) ? '绘图任务失败' : '绘图任务处理中' }}</span>
              <span v-if="msg.imageTask.progress != null">{{ msg.imageTask.progress }}%</span>
              <button v-if="['failed', 'timeout'].includes(msg.imageTask.status)" type="button" @click.stop="$emit('retry-image-task', msg)">重试</button>
              <small v-if="msg.imageTask.error">{{ msg.imageTask.error }}</small>
            </div>

            <div v-else-if="msg.role === 'user'" class="text-content user-text" :style="getMessageTextStyle()">{{ msg.content }}</div>
            
            <!-- 临时邮箱卡片 -->
            <div v-else-if="msg.isTempEmail">
                <div class="text-content" :style="getMessageTextStyle({ marginBottom: '12px' })">
                  <MarkdownRenderer :content="msg.content" :protect-selection="isStreamingLastMessage(msg, index)" :enhance-formulas-on-render="!isStreamingLastMessage(msg, index)" @copy-code="handleCopyCodeEvent" @preview-formula="handleFormulaPreview" @preview-image="openImage" />
                </div>
                <TempEmailCard 
                    :msg="msg" 
                    :index="index" 
                    @copy="copyMessage" 
                />
            </div>
            
            <!-- 本地 Agent 卡片只在雨见浏览器里显示;网页端(含云同步过来的记录)按普通文本显示 -->
            <div v-else-if="msg.role === 'assistant' && msg.localAgent && hasLocalAgentBridge" class="text-content" :style="getMessageTextStyle()">
              <LocalAgentCard
                :msg="msg"
                @progress="$emit('local-agent-progress', index)"
                @finished="$emit('local-agent-finished', index)"
                @answer-normally="$emit('local-agent-answer-normally', index)"
              />
              <MarkdownRenderer v-if="msg.content" :content="msg.content" :protect-selection="msg.localAgent.status === 'running'" @copy-code="handleCopyCodeEvent" @preview-formula="handleFormulaPreview" @preview-image="openImage" />
            </div>

            <div v-else-if="msg.role === 'assistant' && getQuotaLimit(msg)" class="text-content" :style="getMessageTextStyle()">
              <MarkdownRenderer v-if="getQuotaLimit(msg).rest" :content="getQuotaLimit(msg).rest" @copy-code="handleCopyCodeEvent" @preview-formula="handleFormulaPreview" @preview-image="openImage" />
              <QuotaLimitCard
                :limit="getQuotaLimit(msg).limit"
                :can-ask-again="index === messages.length - 1 && !isSending && !!findPreviousUserMessage(index)"
                @ask-again="askAgainAfterQuota(index)"
              />
            </div>

            <div v-else class="text-content" :style="getMessageTextStyle()">
              <MarkdownRenderer :content="msg.content":protect-selection="isStreamingLastMessage(msg, index)" :enhance-formulas-on-render="!isStreamingLastMessage(msg, index)" :editable-code="canEditCode(msg, index)" @copy-code="handleCopyCodeEvent" @preview-formula="handleFormulaPreview" @preview-image="openImage" @edit-code="editCodeBlock(index, msg, $event)" />
            </div>

            <div v-if="msg.role === 'assistant' && msg.workspaceArtifacts && msg.workspaceArtifacts.length" class="workspace-artifacts">
              <div v-for="artifact in msg.workspaceArtifacts" :key="artifact.file_id" class="workspace-artifact-row">
                <div class="workspace-artifact-icon">FILE</div>
                <div class="workspace-artifact-info">
                  <strong>{{ artifact.name }}</strong>
                  <span>{{ formatArtifactSize(artifact.size) }} · 已保存到本地工作区</span>
                </div>
                <button type="button" @click.stop="downloadWorkspaceArtifact(artifact)">下载</button>
                <button type="button" @click.stop="pinWorkspaceArtifact(artifact)">固定</button>
              </div>
            </div>

            <div
              v-if="msg.role === 'assistant' && (msg.traceId || msg.content || msg.imageUrl || msg.isTempEmail || (msg.attachments && msg.attachments.length > 0))"
              class="assistant-meta"
            >
              <span class="ai-generated-text">
                {{ $t('browserAgentChat.aiGenerated') }}
              </span>
              <div v-if="msg.traceId" class="trace-actions">
                <div
                  class="trace-id-chip"
                  :title="$t('browserAgentChat.copyTraceId', { id: msg.traceId })"
                  @click.stop="handleTraceIdClick(msg.traceId)"
                >
                  ID: {{ msg.traceId }}
                </div>
                <span
                  class="report-trace-text"
                  role="button"
                  tabindex="0"
                  @click.stop="openReportDialog(msg.traceId)"
                  @keydown.enter.stop="openReportDialog(msg.traceId)"
                  @keydown.space.prevent.stop="openReportDialog(msg.traceId)"
                >
                  {{ $t('browserAgentChat.report') }}
                </span>
              </div>
            </div>

            <div v-if="msg.role === 'assistant' && msg.scriptArtifact && msg.scriptArtifact.source" class="userscript-result-actions">
              <div class="userscript-result-info">
                <span class="userscript-result-icon" aria-hidden="true">&lt;/&gt;</span>
                <div>
                  <strong>{{ $t('browserAgentChat.userscriptReady') }}</strong>
                  <span>{{ $t('browserAgentChat.userscriptVersion', { version: msg.scriptArtifact.version || 1 }) }}</span>
                </div>
              </div>
              <div class="userscript-result-buttons">
                <a
                  v-if="msg.scriptArtifact.url || msg.scriptArtifact.publishedUrl"
                  class="userscript-result-link"
                  :href="msg.scriptArtifact.url || msg.scriptArtifact.publishedUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                >
                  {{ $t('browserAgentChat.installUserscript') }}
                </a>
                <button type="button" class="userscript-edit-button" @click.stop="editUserscript(index, msg)">
                  {{ $t('browserAgentChat.editUserscript') }}
                </button>
              </div>
            </div>

            <!-- Command Confirmation Embedded -->
            <div v-if="isConfirmDialogOpen && index === messages.length - 1 && msg.role === 'assistant'" class="command-embed-container">
               <div class="command-embed-header">
                  <span class="command-icon">⚡</span>
                  <span>{{ $t('browserAgentChat.commandRequest') }}</span>
               </div>
               <div class="command-embed-content">
                  <pre>{{ pendingCommand }}</pre>
               </div>
               <div class="command-embed-actions">
                  <button class="embed-btn cancel" @click="cancelCommand">{{ $t('browserAgentChat.reject') }}</button>
                  <button class="embed-btn confirm" @click="confirmCommand">{{ $t('browserAgentChat.allowExecution') }}</button>
               </div>
            </div>

            <!-- 知识库来源提示 -->
            <details v-if="msg.role === 'assistant' && hasKbContext(index) && (!isSending || index !== messages.length - 1)" class="kb-sources-wrapper">
              <summary class="kb-sources-header-row">
                <span class="kb-sources-title">{{ $t('browserAgentChat.kbSources') }}</span>
                <span class="kb-sources-divider">|</span>
                <button type="button" class="kb-sources-quote-btn" @click.stop="showKbDetails(index)">
                  <svg class="kb-quote-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14.017 18L14.017 10.609C14.017 4.905 17.748 1.039 23 0L23.995 2.151C21.563 3.068 20 5.789 20 8H24V18H14.017ZM0 18L0 10.609C0 4.905 3.748 1.038 9 0L9.996 2.151C7.563 3.068 6 5.789 6 8H9.983L9.983 18L0 18Z"/>
                  </svg>
                  <span class="kb-quote-text">{{ $t('browserAgentChat.quotedSegments', { count: getKbContextCount(index) }) }}</span>
                </button>
                <svg class="kb-sources-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </summary>
              
              <div class="kb-sources-grid">
                <div v-for="(item, i) in messages[index - 1].kbContext" :key="'link-'+i" class="kb-source-card" @click.stop="openSourceLink(item.document_name)">
                  <div class="kb-source-card-icon-box">
                    <svg class="kb-source-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  </div>
                  <span class="kb-source-card-text">{{ item.document_name || item.title || $t('browserAgentChat.unknownSource') }}</span>
                </div>
              </div>
            </details>

            <!-- 搜索建议 -->
            <div v-if="msg.role === 'user' && msg.searchSuggestion" class="search-suggestion-badge" @click="handleSearchSuggestionClick(msg.searchSuggestion)">
              <div class="search-suggestion-icon">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>
              <span class="search-suggestion-text">{{ $t('browserAgentChat.searchSuggestion', { engine: getSearchEngineLabel(msg.searchSuggestion.engine), keyword: msg.searchSuggestion.keyword }) }}</span>
              <svg class="search-suggestion-arrow" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </div>
          
          <div v-if="msg.role === 'user'" class="message-actions" :class="{ visible: activeMessageIndex === index || isMobile }">
            <button class="action-btn" @click.stop="copyMessage(msg.content)">{{ $t('browserAgentChat.copy') }}</button>
            <button class="action-btn" @click.stop="editMessage(index, msg)">{{ $t('browserAgentChat.edit') }}</button>
          </div>
          <div v-if="msg.role === 'assistant'" class="message-actions" :class="{ visible: activeMessageIndex === index || isMobile }">
             <button class="action-btn" @click.stop="toggleSpeak(index, msg.content)">
               {{ speakingIndex === index ? $t('browserAgentChat.stopSpeaking') : $t('browserAgentChat.speak') }}
             </button>
             <button class="action-btn" @click.stop="copyMessage(msg.content)">{{ $t('browserAgentChat.copy') }}</button>
             <button v-if="showKnowledgeBaseAction" class="action-btn" @click.stop="saveToKnowledgeBase">{{ $t('browserAgentChat.knowledgeBase') }}</button>
             <div v-if="showExport" class="export-dropdown-wrapper">
                 <button class="action-btn" @click.stop="toggleExportMenu(index)">{{ $t('browserAgentChat.export') }} ▼</button>
                 <div v-if="exportMenuIndex === index" class="export-menu">
                   <div class="export-menu-content">
                      <div class="export-item" @click.stop="exportMessage(index, msg.content, 'pdf')">{{ $t('browserAgentChat.exportPdf') }}</div>
                      <div class="export-item" @click.stop="exportMessage(index, msg.content, 'word')">{{ $t('browserAgentChat.exportWord') }}</div>
                      <div class="export-item" @click.stop="exportMessage(index, msg.content, 'markdown')">{{ $t('browserAgentChat.exportMarkdown') }}</div>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </template>

    <div v-if="selectTextSuggestion" class="message-row assistant">
      <div class="message-wrapper select-text-message-wrapper">
        <div class="message-content select-text-message">
          <div class="select-text-message-header">
            <div class="select-text-message-identity">
              <div class="select-text-message-avatar">✨</div>
              <span class="select-text-message-name">{{ $t('browserAgentChat.aiAssistant') }}</span>
            </div>
            <button class="select-text-close-btn" @click.stop="closeSelectTextSuggestion">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
          <div class="select-text-message-body">
            {{ $t('browserAgentChat.selectedTextPrefix') }}<span class="select-text-highlight">“{{ selectTextSuggestion.selectText }}”</span>{{ $t('browserAgentChat.selectedTextSuffix') }}
            <button class="select-text-fill-btn" :title="$t('browserAgentChat.fillInput')" @click.stop="fillSelectText">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 7L7 17"></path>
                <path d="M17 17H7V7"></path>
              </svg>
            </button>
          </div>
          <div class="select-text-message-actions">
            <button
              v-for="(cmd, index) in selectTextSuggestion.textCommands"
              :key="'select-text-cmd-' + index"
              class="select-text-action-chip"
              @click.stop="handleSelectTextCommandClick(cmd)"
            >
              {{ cmd }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="reportDialog.visible"
      class="report-dialog-overlay"
      @click.self="closeReportDialog"
    >
      <div class="report-dialog">
        <div class="report-dialog-header">
          <div class="report-dialog-title-group">
            <h3>{{ $t('browserAgentChat.reportDialogTitle') }}</h3>
            <p>{{ $t('browserAgentChat.reportDialogTraceId', { id: reportDialog.traceId }) }}</p>
          </div>
          <button
            type="button"
            class="report-dialog-close"
            :disabled="reportDialog.submitting"
            @click="closeReportDialog"
          >
            &times;
          </button>
        </div>

        <div class="report-form">
          <div class="report-field">
            <label class="report-field-label">{{ $t('browserAgentChat.reportTypeLabel') }}</label>
            <div class="report-type-list">
              <button
                v-for="option in reportTypeOptions"
                :key="option.value"
                type="button"
                :class="['report-type-option', { active: reportDialog.type === option.value }]"
                @click="reportDialog.type = option.value"
              >
                {{ $t(option.labelKey) }}
              </button>
            </div>
          </div>

          <div class="report-field">
            <label class="report-field-label" for="trace-report-reason">{{ $t('browserAgentChat.reportReasonLabel') }}</label>
            <textarea
              id="trace-report-reason"
              ref="reportReasonInput"
              v-model="reportDialog.reason"
              class="report-reason-input"
              :placeholder="$t('browserAgentChat.reportReasonPlaceholder')"
              :disabled="reportDialog.submitting"
            ></textarea>
          </div>

          <div v-if="reportDialog.error" class="report-form-error">
            {{ reportDialog.error }}
          </div>

          <div class="report-dialog-actions">
            <button
              type="button"
              class="report-dialog-btn secondary"
              :disabled="reportDialog.submitting"
              @click="closeReportDialog"
            >
              {{ $t('browserAgentChat.reportCancel') }}
            </button>
            <button
              type="button"
              class="report-dialog-btn primary"
              :disabled="reportDialog.submitting"
              @click="submitTraceReport"
            >
              {{ reportDialog.submitting ? $t('browserAgentChat.reportSubmitting') : $t('browserAgentChat.reportSubmit') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { jsPDF } from 'jspdf';
import MarkdownRenderer from './MarkdownRenderer.vue';
import TempEmailCard from './TempEmailCard.vue';
import QuotaLimitCard from './QuotaLimitCard.vue';
import LocalAgentCard from './LocalAgentCard.vue';
import { submitTraceReport as submitTraceReportRequest } from '@/api/report';
import { workspaceService } from '@/utils/workspace';

const TRACE_REPORT_TYPES = [
  { value: '与事实不符', labelKey: 'browserAgentChat.reportTypeFactuallyIncorrect' },
  { value: '违法违规', labelKey: 'browserAgentChat.reportTypeIllegal' },
  { value: '诈骗或误导', labelKey: 'browserAgentChat.reportTypeFraud' },
  { value: '色情低俗', labelKey: 'browserAgentChat.reportTypePorn' },
  { value: '暴力仇恨', labelKey: 'browserAgentChat.reportTypeViolence' },
  { value: '隐私侵权', labelKey: 'browserAgentChat.reportTypePrivacy' },
  { value: '其他问题', labelKey: 'browserAgentChat.reportTypeOther' }
];

export default {
  name: 'BrowserAgentChatMessages',
  components: {
    MarkdownRenderer,
    TempEmailCard,
    QuotaLimitCard,
    LocalAgentCard
  },
  props: {
    messages: {
      type: Array,
      required: true
    },
    userMessageCount: {
      type: Number,
      required: true
    },
    isSending: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    activeMessageIndex: {
      type: Number,
      default: null
    },
    speakingIndex: {
      type: Number,
      default: null
    },
    exportMenuIndex: {
      type: Number,
      default: null
    },
    showKnowledgeBaseAction: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: Number,
      default: 16
    },
    isConfirmDialogOpen: {
      type: Boolean,
      default: false
    },
    pendingCommand: {
      type: String,
      default: ''
    },
    selectTextSuggestion: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      reportDialog: {
        visible: false,
        traceId: '',
        type: TRACE_REPORT_TYPES[0].value,
        reason: '',
        error: '',
        submitting: false
      }
    };
  },
  computed: {
    // window.rainseeBrowser.localAgent 只由雨见浏览器 preload 注入
    hasLocalAgentBridge() {
      return !!(typeof window !== 'undefined' && window.rainseeBrowser && window.rainseeBrowser.localAgent);
    },
    reportTypeOptions() {
      return TRACE_REPORT_TYPES;
    },
    // OpenHarmony 环境下隐藏导出功能
    showExport() {
      return typeof navigator === 'undefined' || !/OpenHarmony/i.test(navigator.userAgent || '');
    }
  },
  methods: {
    getCompanionNames(msg, index = -1) {
      if (!msg) return '';
      let snapshots = Array.isArray(msg.companionSnapshots) ? msg.companionSnapshots : [];
      // Older assistant messages may not have their own snapshot. Their
      // preceding user message still carries the send-time snapshot.
      if (!snapshots.length && index > 0 && this.messages[index - 1]) {
        snapshots = Array.isArray(this.messages[index - 1].companionSnapshots)
          ? this.messages[index - 1].companionSnapshots
          : [];
      }
      const names = snapshots
        .map(snapshot => typeof snapshot === 'string' ? snapshot : snapshot && snapshot.name)
        .filter(Boolean);
      return Array.from(new Set(names)).join(' · ');
    },
    formatArtifactSize(bytes) {
      const value = Number(bytes) || 0;
      if (value < 1024) return `${value} B`;
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    },
    async downloadWorkspaceArtifact(artifact) {
      try {
        await workspaceService.downloadFile(artifact.file_id);
      } catch (error) {
        console.warn('Failed to download workspace artifact:', error);
      }
    },
    async pinWorkspaceArtifact(artifact) {
      try {
        await workspaceService.setPinned(artifact.file_id, true, 'default');
      } catch (error) {
        console.warn('Failed to pin workspace artifact:', error);
      }
    },
    shouldHideMessage(msg, index) {
      if (!msg) return true;
      if (msg.hidden || msg.internal || msg.role === 'system') return true;
      return index === 0 && msg.role === 'assistant';
    },
    getMessageKey(msg, index) {
      return (msg && (msg.id || msg.timestamp)) || index;
    },
    getMessageTextStyle(extraStyles = {}) {
      return {
        fontSize: `${this.fontSize}px`,
        ...extraStyles
      };
    },
    isStreamingLastMessage(msg, index) {
      return this.isSending && msg && msg.role === 'assistant' && index === this.messages.length - 1;
    },
    handleMouseEnter(index) {
      this.$emit('mouse-enter', index);
    },
    handleMouseLeave(index) {
      this.$emit('mouse-leave', index);
    },
    handleMessageClick(index) {
      this.$emit('message-click', index);
    },
    handleCopyCodeEvent(code) {
      this.$emit('copy-code', code);
    },
    handleFormulaPreview(formula) {
      this.$emit('preview-formula', formula);
    },
    openImage(url) {
      this.$emit('open-image', url);
    },
    openFile(url) {
      if (url) {
        window.open(url, '_blank');
      }
    },
    cancelScraping() {
      this.$emit('cancel-scraping');
    },
    useKnowledgeBaseDirectly() {
      this.$emit('use-knowledge-base-directly');
    },
    skipKnowledgeBase() {
      this.$emit('skip-knowledge-base');
    },
    copyMessage(content) {
      this.$emit('copy-message', content);
    },
    closeExportMenu(index) {
      if (this.exportMenuIndex === index) {
        this.$emit('toggle-export-menu', index);
      }
    },
    createPlainTextExportNode(content = '') {
      const plainTextNode = document.createElement('div');
      plainTextNode.style.whiteSpace = 'pre-wrap';
      plainTextNode.style.lineHeight = '1.6';
      plainTextNode.style.color = '#333';
      plainTextNode.style.fontFamily = '"SimSun", "宋体", serif';
      plainTextNode.textContent = content;
      return plainTextNode;
    },
    getExportContentNode(index, content) {
      const messageRow = this.$el.querySelector(`.message-row[data-message-index="${index}"]`);
      const contentEl = messageRow && (
        messageRow.querySelector('.text-content .markdown-body') ||
        messageRow.querySelector('.text-content')
      );

      if (contentEl) {
        return contentEl.cloneNode(true);
      }

      return this.createPlainTextExportNode(content);
    },
    getExportPlainText(index, content) {
      const messageRow = this.$el.querySelector(`.message-row[data-message-index="${index}"]`);
      const contentEl = messageRow && (
        messageRow.querySelector('.text-content .markdown-body') ||
        messageRow.querySelector('.text-content')
      );

      if (contentEl) {
        const renderedText = (contentEl.innerText || contentEl.textContent || '').trim();
        if (renderedText) {
          return renderedText;
        }
      }

      return content;
    },
    isAgentMode() {
      if (typeof window === 'undefined') {
        return false;
      }

      try {
        return new URLSearchParams(window.location.search).get('mode') === 'agent';
      } catch (error) {
        console.warn('Failed to detect agent mode:', error);
        return false;
      }
    },
    async uploadBlobToPicmain(blob, filename) {
      const formData = new FormData();
      formData.append('file', blob, filename);

      const response = await fetch('https://picmain.rainsee.cn/api.php', {
        method: 'POST',
        body: formData
      });

      const rawResponse = await response.text();
      let result = null;

      try {
        result = rawResponse ? JSON.parse(rawResponse) : null;
      } catch (error) {
        throw new Error(`Upload API returned invalid JSON: ${rawResponse.slice(0, 200)}`);
      }

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      if (result && result.fileurl) {
        return result.fileurl;
      }

      throw new Error((result && (result.msg || result.message)) || 'Failed to get fileurl from upload API');
    },
    async handleExportBlob(blob, filename) {
      if (!this.isAgentMode()) {
        this.downloadBlob(blob, filename);
        return;
      }


        const uploadUrl = await this.uploadBlobToPicmain(blob, filename);
        location.href = uploadUrl;

    },
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    wrapPdfTextLine(ctx, line, maxWidth) {
      if (!line) {
        return [''];
      }

      const wrappedLines = [];
      let currentLine = '';

      for (const char of line) {
        const normalizedChar = char === '\t' ? '    ' : char;
        const nextLine = currentLine + normalizedChar;

        if (!currentLine || ctx.measureText(nextLine).width <= maxWidth) {
          currentLine = nextLine;
        } else {
          wrappedLines.push(currentLine);
          currentLine = normalizedChar;
        }
      }

      if (currentLine) {
        wrappedLines.push(currentLine);
      }

      return wrappedLines;
    },
    async exportPdfFromText(content, filename) {
      const normalizedText = (content || '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .trim();

      const canvas = document.createElement('canvas');
      const canvasWidth = 1240;
      const canvasHeight = 1754;
      const marginX = 90;
      const marginTop = 96;
      const marginBottom = 84;
      const fontSize = 28;
      const lineHeight = 42;
      const footerFontSize = 20;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Unable to create PDF canvas context');
      }

      ctx.font = `${fontSize}px "SimSun", "宋体", serif`;
      const maxWidth = canvasWidth - marginX * 2;
      const sourceLines = normalizedText ? normalizedText.split('\n') : [''];
      const wrappedLines = [];

      sourceLines.forEach((line) => {
        wrappedLines.push(...this.wrapPdfTextLine(ctx, line, maxWidth));
      });

      const maxLinesPerPage = Math.max(
        1,
        Math.floor((canvasHeight - marginTop - marginBottom) / lineHeight)
      );
      const pdf = new jsPDF({
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait'
      });

      let pageNumber = 0;

      for (let start = 0; start < wrappedLines.length; start += maxLinesPerPage) {
        if (pageNumber > 0) {
          pdf.addPage();
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        ctx.fillStyle = '#222222';
        ctx.font = `${fontSize}px "SimSun", "宋体", serif`;
        ctx.textBaseline = 'top';
        ctx.textAlign = 'left';

        const pageLines = wrappedLines.slice(start, start + maxLinesPerPage);
        pageLines.forEach((line, lineIndex) => {
          const y = marginTop + lineIndex * lineHeight;
          if (line) {
            ctx.fillText(line, marginX, y);
          }
        });

        ctx.fillStyle = '#888888';
        ctx.font = `${footerFontSize}px "SimSun", "宋体", serif`;
        ctx.textAlign = 'right';
        ctx.fillText(`${pageNumber + 1}`, canvasWidth - marginX, canvasHeight - 48);

        pdf.addImage(canvas, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
        pageNumber += 1;
      }

      const blob = pdf.output('blob');
      await this.handleExportBlob(blob, filename);
    },
    buildWordExportHtml(contentHtml) {
      return `
        <html xmlns:o='urn:schemas-microsoft-com:office:office'
              xmlns:w='urn:schemas-microsoft-com:office:word'
              xmlns='http://www.w3.org/TR/REC-html40'>
        <head><meta charset='utf-8'><title>Export Document</title>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <style>
          @page Section1 {
            size: 21.0cm 29.7cm;
            margin: 2.54cm 3.17cm 2.54cm 3.17cm;
            mso-header-margin: 35.4pt;
            mso-footer-margin: 35.4pt;
            mso-paper-source: 0;
          }
          div.Section1 {
            page: Section1;
          }
          body { font-family: 'SimSun', '宋体', serif; line-height: 1.6; color: #333; }
          pre { background: #f5f5f5; padding: 10px; border-radius: 5px; white-space: pre-wrap; font-family: monospace; }
          code { background: #f5f5f5; padding: 2px 5px; border-radius: 3px; font-family: monospace; }
          table { border-collapse: collapse; width: 100%; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          blockquote { border-left: 4px solid #ddd; padding-left: 10px; color: #666; margin: 10px 0; }
          img { max-width: 100%; height: auto; }
        </style>
        </head><body><div class="Section1">${contentHtml}</div></body></html>
      `;
    },
    openReportDialog(traceId) {
      this.reportDialog.visible = true;
      this.reportDialog.traceId = traceId;
      this.reportDialog.type = TRACE_REPORT_TYPES[0].value;
      this.reportDialog.reason = '';
      this.reportDialog.error = '';
      this.reportDialog.submitting = false;

      this.$nextTick(() => {
        if (this.$refs.reportReasonInput) {
          this.$refs.reportReasonInput.focus();
        }
      });
    },
    closeReportDialog() {
      if (this.reportDialog.submitting) {
        return;
      }

      this.reportDialog.visible = false;
      this.reportDialog.traceId = '';
      this.reportDialog.type = TRACE_REPORT_TYPES[0].value;
      this.reportDialog.reason = '';
      this.reportDialog.error = '';
      this.reportDialog.submitting = false;
    },
    async submitTraceReport() {
      const reason = (this.reportDialog.reason || '').trim();
      if (!reason) {
        this.reportDialog.error = this.$t('browserAgentChat.reportReasonRequired');
        return;
      }

      this.reportDialog.error = '';
      this.reportDialog.submitting = true;

      try {
        await submitTraceReportRequest({
          traceId: this.reportDialog.traceId,
          title: this.reportDialog.type,
          reason
        });
        this.reportDialog.submitting = false;
        this.closeReportDialog();
        alert(this.$t('browserAgentChat.reportSuccessAlert'));
      } catch (error) {
        console.error('Failed to submit trace report:', error);
        this.reportDialog.submitting = false;
        this.reportDialog.error = this.$t('browserAgentChat.reportFailed');
      }
    },
    handleTraceIdClick(traceId) {
      this.copyMessage(traceId);
      alert(this.$t('browserAgentChat.traceCopiedAlert'));
    },
    editMessage(index, msg) {
      this.$emit('edit-message', index, msg);
    },
    // 后端每日免费次数用尽时追加的固定文案(AiAssistantv5 daily_limit),识别后渲染额度卡片;
    // 按文案识别而非消息字段,历史会话里的旧消息同样生效。
    getQuotaLimit(msg) {
      const content = typeof msg.content === 'string' ? msg.content : '';
      const match = content.match(/今日调用次数已达上限\s*[(（](\d+)次[)）][^\n]*/);
      if (!match) return null;
      return {
        limit: Number(match[1]) || 0,
        rest: content.replace(match[0], '').trim()
      };
    },
    findPreviousUserMessage(index) {
      for (let i = index - 1; i >= 0; i -= 1) {
        if (this.messages[i] && this.messages[i].role === 'user') return { index: i, msg: this.messages[i] };
      }
      return null;
    },
    askAgainAfterQuota(index) {
      const prev = this.findPreviousUserMessage(index);
      if (prev) this.editMessage(prev.index, prev.msg);
    },
    editUserscript(index, msg) {
      this.$emit('edit-userscript', index, msg);
    },
    canEditCode(msg, index) {
      return msg.role === 'assistant' && !this.isStreamingLastMessage(msg, index) && !!this.$listeners['edit-code'];
    },
    editCodeBlock(index, msg, block) {
      this.$emit('edit-code', index, msg, block);
    },
    toggleSpeak(index, content) {
      this.$emit('toggle-speak', index, content);
    },
    toggleExportMenu(index) {
      this.$emit('toggle-export-menu', index);
    },
    saveToKnowledgeBase() {
      this.$emit('save-to-kb');
    },
    async exportMessage(index, content, type) {
      const safeContent = typeof content === 'string' ? content : '';
      this.closeExportMenu(index);

      try {
        const exportBaseName = `chat-export-${Date.now()}`;

        if (type === 'markdown') {
          const blob = new Blob([safeContent], { type: 'text/markdown;charset=utf-8' });
          await this.handleExportBlob(blob, `${exportBaseName}.md`);
          return;
        }

        let contentNode = null;

        if (type === 'pdf') {
          const exportText = this.getExportPlainText(index, safeContent);
          await this.exportPdfFromText(exportText, `${exportBaseName}.pdf`);
          return;

          const wrapper = document.createElement('div');
          wrapper.style.position = 'absolute';
          wrapper.style.left = '-9999px';
          wrapper.style.top = '0';
          wrapper.style.width = '700px';
          wrapper.style.background = '#fff';

          contentNode.style.fontFamily = '"SimSun", "宋体", serif';
          wrapper.appendChild(contentNode);
          document.body.appendChild(wrapper);

          const options = {
            margin: [15, 20, 15, 20],
            filename: `chat-export-${Date.now()}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
          };

          try {
            await html2pdf().set(options).from(contentNode).save();
          } finally {
            if (document.body.contains(wrapper)) {
              document.body.removeChild(wrapper);
            }
          }
          return;
        }

        if (type === 'word') {
          if (!contentNode) {
            contentNode = this.getExportContentNode(index, safeContent);
          }
          const sourceHTML = this.buildWordExportHtml(contentNode.innerHTML);
          const blob = new Blob(['\ufeff', sourceHTML], { type: 'application/msword' });
          await this.handleExportBlob(blob, `${exportBaseName}.doc`);
        }
      } catch (error) {
        console.error('Failed to export message:', error);
        const message = error && error.message ? error.message : 'unknown error';
        alert(this.$t('chatLayout.exportFailed', { message }));
      }
    },
    cancelCommand() {
      this.$emit('cancel-command');
    },
    cssPreviewTitle(preview) {
      if (!preview) return '';
      if (preview.state === 'applied') return '样式已保存';
      if (preview.state === 'discarded') return '已放弃此预览';
      if (preview.state === 'undone') return '已撤销此样式';
      if (preview.state === 'superseded') return '已被新的预览替换';
      return '样式预览中，确认后保存';
    },
    confirmCommand() {
      this.$emit('confirm-command');
    },
    showKbDetails(index) {
      this.$emit('show-kb-details', index);
    },
    openSourceLink(link) {
      this.$emit('open-source-link', link);
    },
    handleSearchSuggestionClick(suggestion) {
      this.$emit('search-suggestion-click', suggestion);
    },
    isKnowledgeBaseCommand(command) {
      if (typeof command !== 'string') {
        return false;
      }

      const normalized = command.trim();
      if (!normalized) {
        return false;
      }

      const localized = this.$t('browserAgent.quickActions.addToKnowledgeBase');
      return normalized === localized || normalized === '加入知识库';
    },
    handleSelectTextCommandClick(command) {
      if (!this.selectTextSuggestion || !this.selectTextSuggestion.selectText) return;
      if (this.isKnowledgeBaseCommand(command)) {
        this.$emit('save-select-text-to-kb', this.selectTextSuggestion.selectText);
        return;
      }
      this.$emit('select-text-command-click', command, this.selectTextSuggestion.selectText);
    },
    fillSelectText() {
      if (!this.selectTextSuggestion || !this.selectTextSuggestion.selectText) return;
      this.$emit('fill-select-text', this.selectTextSuggestion.selectText);
    },
    closeSelectTextSuggestion() {
      this.$emit('close-select-text');
    },
    isPendingStatus(chatStatus) {
      return ['analyzing', 'scraping', 'summarizing', 'knowledge_base', 'kb_relevance'].includes(chatStatus);
    },
    isSpinnerStatus(chatStatus) {
      return ['analyzing', 'kb_relevance'].includes(chatStatus);
    },
    isSkippableStatus(chatStatus) {
      return ['scraping', 'knowledge_base'].includes(chatStatus);
    },
    hasKbContext(index) {
      if (index === 0) return false;
      const prevMsg = this.messages[index - 1];
      return prevMsg && prevMsg.role === 'user' && prevMsg.kbContext && prevMsg.kbContext.length > 0;
    },
    getKbContextCount(index) {
      if (index === 0) return 0;
      const prevMsg = this.messages[index - 1];
      if (prevMsg && prevMsg.role === 'user' && prevMsg.kbContext) {
        return prevMsg.kbContext.length;
      }
      return 0;
    },
    getSearchEngineLabel(engine) {
      return engine === 'baidu'
        ? this.$t('browserAgentChat.engines.baidu')
        : this.$t('browserAgentChat.engines.mita');
    }
  }
}
</script>

<style scoped>
.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
  width: 100%;
  /* Keep the conversation aligned with the centered composer on desktop. */
  max-width: min(768px, 100%);
  margin-left: auto;
  margin-right: auto;
  min-width: 0;
  box-sizing: border-box;
  padding-bottom:160px;
}

.message-row {
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  min-width: 0;
}

.message-row.user {
  justify-content: flex-end;
}

.message-row.assistant {
  justify-content: flex-start;
}

.message-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.message-row.user .message-wrapper {
  max-width: 85%;
  align-items: flex-end;
}

.message-row.assistant .message-wrapper {
  align-items: flex-start;
}

.companion-message-label {
  display: inline-flex;
  align-items: center;
  margin: 0 0 6px 4px;
  color: #5a6bb9;
  font-size: 12px;
  font-weight: 650;
  line-height: 18px;
}

[data-theme="dark"] .companion-message-label {
  color: #b8c4ff;
}

.userscript-result-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e9ee;
}

.userscript-result-info,
.userscript-result-buttons {
  display: flex;
  align-items: center;
  gap: 9px;
}

.userscript-result-info {
  min-width: 0;
}

.userscript-result-info > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.userscript-result-info strong {
  color: #263445;
  font-size: 13px;
}

.userscript-result-info span:not(.userscript-result-icon) {
  color: #7a8796;
  font-size: 11px;
}

.userscript-result-icon {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: #e2f3f0;
  color: #0f766e;
  font: 700 11px Consolas, monospace;
}

.userscript-result-link,
.userscript-edit-button {
  min-height: 34px;
  padding: 0 11px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 650;
  text-decoration: none;
  cursor: pointer;
}

.userscript-result-link {
  display: inline-flex;
  align-items: center;
  border: 1px solid #cbd8df;
  color: #425466;
  background: #fff;
}

.userscript-result-link:hover {
  background: #f3f7f8;
}

.userscript-edit-button {
  border: 1px solid #0f766e;
  background: #0f766e;
  color: #fff;
}

.userscript-edit-button:hover {
  background: #0b625c;
}

@media (max-width: 560px) {
  .userscript-result-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .userscript-result-buttons {
    width: 100%;
  }

  .userscript-result-link,
  .userscript-edit-button {
    flex: 1;
    justify-content: center;
    text-align: center;
  }
}

.message-content {
  position: relative;
  border-radius: 16px;
  padding: 12px 16px;
  font-size: 15px;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: #FFFFFF;
  color: #1C1C1E;
 border: 1px solid #e5edff;
}

.text-content {
  max-width: 100%;
  min-width: 0;
}

.message-row.user .message-content {
  background: #1D44FF;
  color: #FFFFFF;
  border-bottom-right-radius: 4px;
}

.message-row.assistant .message-content {
  border-bottom-left-radius: 4px;
}

.text-content.user-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.assistant-meta {
  margin-top: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  max-width: 100%;
}

.ai-generated-text {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  color: #8A8A8E;
  font-size: 10px;
  line-height: 1.4;
  opacity: 0.72;
}

.trace-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
}

.trace-id-chip {
  display: inline-flex;
  align-self: flex-start;
  max-width: 100%;
  word-break: break-all;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: #8A8A8E;
  font-size: 10px;
  line-height: 1.4;
  cursor: pointer;
  border: none;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.trace-id-chip:hover {
  opacity: 1;
  background: transparent;
  border: none;
}

.report-trace-text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  color: #DC2626;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  opacity: 0.72;
  transition: opacity 0.2s ease, color 0.2s ease;
}

.report-trace-text:hover,
.report-trace-text:focus {
  color: #B91C1C;
  opacity: 1;
  outline: none;
}

.report-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.46);
  backdrop-filter: blur(6px);
}

.report-dialog {
  width: min(100%, 480px);
  background: #FFFFFF;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
  border: 1px solid rgba(226, 232, 240, 0.9);
  overflow: hidden;
}

.report-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #E5E7EB;
}

.report-dialog-title-group h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.report-dialog-title-group p {
  margin: 6px 0 0;
  font-size: 12px;
  color: #6B7280;
  word-break: break-all;
}

.report-dialog-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #9CA3AF;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
}

.report-dialog-close:hover:not(:disabled) {
  background: #F3F4F6;
  color: #111827;
}

.report-dialog-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.report-form {
  padding: 18px 20px 20px;
}

.report-field + .report-field {
  margin-top: 16px;
}

.report-field-label {
  display: block;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.report-type-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.report-type-option {
  padding: 8px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 999px;
  background: #F9FAFB;
  color: #4B5563;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.report-type-option:hover {
  border-color: rgba(220, 38, 38, 0.28);
  color: #DC2626;
}

.report-type-option.active {
  border-color: #DC2626;
  background: rgba(239, 68, 68, 0.1);
  color: #B91C1C;
}

.report-reason-input {
  width: 100%;
  min-height: 116px;
  padding: 12px 14px;
  border: 1px solid #D1D5DB;
  border-radius: 14px;
  background: #FFFFFF;
  color: #111827;
  font: inherit;
  line-height: 1.6;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.report-reason-input:focus {
  outline: none;
  border-color: #EF4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}

.report-reason-input:disabled {
  background: #F9FAFB;
  cursor: not-allowed;
}

.report-form-error {
  margin-top: 14px;
  font-size: 12px;
  color: #DC2626;
}

.report-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.report-dialog-btn {
  min-width: 96px;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.report-dialog-btn.secondary {
  border-color: #D1D5DB;
  background: #FFFFFF;
  color: #4B5563;
}

.report-dialog-btn.secondary:hover:not(:disabled) {
  background: #F9FAFB;
  border-color: #9CA3AF;
}

.report-dialog-btn.primary {
  background: #DC2626;
  color: #FFFFFF;
}

.report-dialog-btn.primary:hover:not(:disabled) {
  background: #B91C1C;
}

.report-dialog-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Generating Border Animation */
.message-content.generating-border {
  overflow: hidden;
  background: transparent !important;
  z-index: 1;
}

.message-content.generating-border::before {
  content: "";
  position: absolute;
  top: 50%; left: 50%;
  width: 150vmax; height: 150vmax;
  margin-left: -75vmax; margin-top: -75vmax;
  background: conic-gradient(from 0deg, transparent 0%, transparent 60%, #00f2fe 75%, #4facfe 85%, #f093fb 95%, #f5576c 100%);
  animation: spinBorder 2.5s linear infinite;
  z-index: -2;
  transform-origin: center;
}

.message-content.generating-border::after {
  content: "";
  position: absolute;
  inset: 2px;
  border-radius: 14px;
  background: #FFFFFF;
  z-index: -1;
}

.message-row.assistant .message-content.generating-border::after {
  border-bottom-left-radius: 2px;
}

@keyframes spinBorder {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Browser chrome CSS preview confirmation */
.css-preview-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #C7D2FE;
  border-radius: 8px;
  background: #EEF2FF;
}

.css-preview-card.css-preview-discarded,
.css-preview-card.css-preview-undone,
.css-preview-card.css-preview-superseded {
  border-color: #E5E7EB;
  background: #F9FAFB;
  opacity: 0.75;
}

.css-preview-card.css-preview-applied {
  border-color: #BBF7D0;
  background: #F0FDF4;
}

.css-preview-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  font-size: 13px;
  color: #374151;
}

.css-preview-text strong { font-weight: 600; color: #111827; }
.css-preview-text em { font-style: normal; color: #DC2626; font-size: 12px; }

.css-preview-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
}

.css-preview-btn {
  height: 28px;
  padding: 0 12px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  background: #FFFFFF;
  color: #374151;
  font-size: 12.5px;
  cursor: pointer;
}

.css-preview-btn.primary {
  border-color: #4F46E5;
  background: #4F46E5;
  color: #FFFFFF;
}

.css-preview-btn:disabled { opacity: 0.6; cursor: default; }
.css-preview-btn:focus-visible { outline: 2px solid #6366F1; outline-offset: 2px; }

[data-theme="dark"] .css-preview-card { border-color: #3730A3; background: rgba(79, 70, 229, 0.16); }
[data-theme="dark"] .css-preview-card.css-preview-applied { border-color: #166534; background: rgba(22, 101, 52, 0.18); }
[data-theme="dark"] .css-preview-card.css-preview-discarded,
[data-theme="dark"] .css-preview-card.css-preview-undone,
[data-theme="dark"] .css-preview-card.css-preview-superseded { border-color: #374151; background: #1F2937; }
[data-theme="dark"] .css-preview-text,
[data-theme="dark"] .css-preview-text strong { color: #E5E7EB; }
[data-theme="dark"] .css-preview-btn { border-color: #4B5563; background: #111827; color: #E5E7EB; }
[data-theme="dark"] .css-preview-btn.primary { border-color: #6366F1; background: #6366F1; color: #FFFFFF; }

/* Workspace tool steps */
.workspace-steps {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.workspace-step {
  padding: 0;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background: #F9FAFB;
}

.workspace-step-summary {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 9px 11px;
  cursor: pointer;
  list-style: none;
}

.workspace-step-summary::-webkit-details-marker { display: none; }

.workspace-step-icon {
  display: flex;
  flex: 0 0 auto;
  width: 17px;
  height: 17px;
  margin-top: 1px;
  color: #6B7280;
}

.workspace-step-icon svg {
  width: 17px;
  height: 17px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.workspace-step-running {
  border-color: rgba(37, 99, 235, .25);
  background: rgba(37, 99, 235, .05);
}

.workspace-step-running .workspace-step-icon { color: #2563EB; }
.workspace-step-done .workspace-step-icon { color: #059669; }
.workspace-step-error { border-color: rgba(220, 38, 38, .25); background: rgba(220, 38, 38, .04); }
.workspace-step-error .workspace-step-icon { color: #DC2626; }

.workspace-step-spinner { animation: rotate 1s linear infinite; }

.workspace-step-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  column-gap: 10px;
  min-width: 0;
  width: 100%;
}

.workspace-step-body strong {
  min-width: 0;
  overflow-wrap: anywhere;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
}

.workspace-step-body small {
  color: #6B7280;
  font-size: 11px;
  white-space: nowrap;
}

.workspace-step-body em {
  grid-column: 1 / -1;
  margin-top: 2px;
  color: #6B7280;
  font-size: 11px;
  font-style: normal;
}

.workspace-step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 17px;
  margin-right: 6px;
  border-radius: 50%;
  background: rgba(107, 114, 128, .13);
  color: #6B7280;
  font-size: 10px;
  font-weight: 700;
  vertical-align: -1px;
}

.workspace-step-chevron {
  flex: 0 0 auto;
  margin-top: -2px;
  color: #9CA3AF;
  font-size: 16px;
  line-height: 1;
  transition: transform .18s ease;
}

.workspace-step[open] .workspace-step-chevron { transform: rotate(180deg); }

.workspace-step-detail {
  padding: 0 11px 11px 37px;
  border-top: 1px solid #E5E7EB;
}

.workspace-step-detail-section { margin-top: 9px; }

.workspace-step-detail-section > span {
  display: block;
  margin-bottom: 4px;
  color: #6B7280;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .02em;
}

.workspace-step-detail-section pre {
  max-height: 220px;
  margin: 0;
  padding: 8px 9px;
  overflow: auto;
  border-radius: 5px;
  background: rgba(17, 24, 39, .055);
  color: #4B5563;
  font: 11px/1.5 ui-monospace, SFMono-Regular, Consolas, monospace;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.workspace-step-detail-section code {
  display: inline-block;
  padding: 3px 6px;
  border-radius: 4px;
  background: rgba(17, 24, 39, .055);
  color: #4B5563;
  font: 11px ui-monospace, SFMono-Regular, Consolas, monospace;
}

/* Thinking Block */
.thinking-block {
  margin-bottom: 12px;
  border: 1px solid #E5E5EA;
  border-radius: 8px;
  background: #F9FAFB;
  overflow: hidden;
  max-width: 100%;
  transition: all 0.3s ease;
}

.thinking-block.is-thinking {
  border-color: #DCE0F0;
  box-shadow: 0 0 0 2px rgba(74, 101, 230, 0.1);
}

.thinking-block details summary {
  list-style: none;
  cursor: pointer;
  padding: 10px 12px;
  user-select: none;
}

.thinking-block details summary::-webkit-details-marker {
  display: none;
}

.thinking-header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.thinking-icon {
  display: flex;
  align-items: center;
  color: #8A8A8E;
}

.thinking-block.is-thinking .thinking-icon {
  color: #4A65E6;
  animation: pulse 1.5s infinite;
}

.thinking-title {
  font-size: 13px;
  font-weight: 600;
  color: #4B5563;
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.chevron-icon {
  color: #8A8A8E;
  transition: transform 0.3s ease;
}

.thinking-block details[open] summary .chevron-icon {
  transform: rotate(180deg);
}

.thinking-content {
  padding: 0 12px 12px;
  font-size: 14px;
  color: #6B7280;
  border-top: 1px solid #E5E5EA;
  margin-top: 8px;
  padding-top: 12px;
}

/* Smart Search Status */
.smart-search-status {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
  color: var(--text-secondary);
  font-size: 0.9em;
}
.smart-search-status .spinner {
  animation: rotate 2s linear infinite;
}
.smart-search-status .spinner .path {
  stroke: var(--primary-color);
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}
.cancel-scrape-btn {
  margin-left: 10px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 2px 5px rgba(239, 68, 68, 0.05);
}

.cancel-scrape-btn:hover {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
  transform: translateY(-1px);
}

.cancel-scrape-btn:active {
  transform: translateY(0);
}

.kb-judging-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px;
  margin-left: 4px;
  background: rgba(110, 136, 249, 0.08);
  border: 1px solid rgba(110, 136, 249, 0.16);
  border-radius: 999px;
}

.kb-judging-btn {
  border: 0;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.kb-judging-btn.primary {
  color: #fff;
  background: linear-gradient(135deg, #6E88F9, #8B5CF6);
  box-shadow: 0 4px 10px rgba(110, 136, 249, 0.22);
}

.kb-judging-btn.secondary {
  color: #6B7280;
  background: transparent;
}

.kb-judging-btn:hover {
  transform: translateY(-1px);
}

.kb-judging-btn.secondary:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
}

[data-theme="dark"] .cancel-scrape-btn {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
}

[data-theme="dark"] .cancel-scrape-btn:hover {
  background: #f87171;
  border-color: #f87171;
  box-shadow: 0 4px 12px rgba(248, 113, 113, 0.3);
}

[data-theme="dark"] .kb-judging-actions {
  background: rgba(110, 136, 249, 0.14);
  border-color: rgba(139, 92, 246, 0.3);
}

[data-theme="dark"] .kb-judging-btn.secondary {
  color: #D1D5DB;
}

[data-theme="dark"] .kb-judging-btn.secondary:hover {
  color: #FCA5A5;
  background: rgba(248, 113, 113, 0.16);
}


@keyframes dash {
  0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
  50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
  100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
}

.typing-indicator > span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #8A8A8E;
  animation: bounce 1.4s infinite ease-in-out both;
}

.typing-indicator > span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator > span:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Skeleton Loader */
.image-skeleton-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #F9FAFB;
  border-radius: 8px;
  border: 1px dashed #E5E5EA;
}

.skeleton-image {
  width: 200px;
  height: 200px;
  background: #F3F4F6;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.skeleton-icon {
  color: #D1D5DB;
  z-index: 1;
}

.image-task-status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #6b7280;
  font-size: 12px;
}

.image-task-status button {
  border: 0;
  border-radius: 5px;
  padding: 3px 8px;
  color: #2563eb;
  background: #eff6ff;
  cursor: pointer;
}

.image-task-status small {
  flex-basis: 100%;
  color: #b91c1c;
  overflow-wrap: anywhere;
}

.skeleton-text {
  font-size: 13px;
  color: #6B7280;
  font-weight: 500;
}

/* Message Actions */
.message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
  max-width: 100%;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.message-wrapper:hover .message-actions {
  opacity: 1;
}

.select-text-message-wrapper {
  max-width: 90%;
}

.select-text-message {
  border: 1px solid rgba(74, 101, 230, 0.14);
  box-shadow: 0 6px 18px rgba(74, 101, 230, 0.08), 0 2px 8px rgba(0, 0, 0, 0.03);
}

.select-text-message-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.select-text-message-identity {
  display: flex;
  align-items: center;
  gap: 8px;
}

.select-text-message-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6E88F9 0%, #4A65E6 100%);
  color: #FFFFFF;
  font-size: 10px;
}

.select-text-message-name {
  font-size: 12px;
  font-weight: 600;
  color: #8A8A8E;
}

.select-text-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #C7C7CC;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;
}

.select-text-close-btn:hover {
  color: #FF3B30;
  background: rgba(255, 59, 48, 0.08);
}

.select-text-message-body {
  font-size: 14px;
  line-height: 1.6;
  color: #1C1C1E;
  margin-bottom: 12px;
}

.select-text-fill-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 8px;
  border: none;
  border-radius: 50%;
  background: rgba(74, 101, 230, 0.08);
  color: #4A65E6;
  cursor: pointer;
  vertical-align: middle;
  transition: all 0.2s ease;
}

.select-text-fill-btn:hover {
  background: #4A65E6;
  color: #FFFFFF;
  transform: translateY(-1px);
}

.select-text-highlight {
  color: #4A65E6;
  font-weight: 600;
  background: rgba(74, 101, 230, 0.08);
  padding: 0 4px;
  border-radius: 4px;
}

.select-text-message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.select-text-action-chip {
  padding: 6px 12px;
  border: 1px solid rgba(110, 136, 249, 0.14);
  border-radius: 12px;
  background: rgba(110, 136, 249, 0.06);
  color: #4A65E6;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.select-text-action-chip:hover {
  background: #4A65E6;
  border-color: #4A65E6;
  color: #FFFFFF;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(74, 101, 230, 0.2);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: #8A8A8E;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s, color 0.2s;
}

.action-btn:hover {
  background: #F3F4F6;
  color: #4B5563;
}

.message-actions.visible {
  opacity: 1;
}

.export-dropdown-wrapper {
  position: relative;
}

.export-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  z-index: 100;
  min-width: 120px;
  border: 1px solid #E5E5EA;
  overflow: hidden;
}

.export-menu-content {
  display: flex;
  flex-direction: column;
}

.export-item {
  padding: 10px 16px;
  font-size: 13px;
  color: #4B5563;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}

.export-item:hover {
  background: #F9FAFB;
  color: #1D44FF;
}

.message-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  max-width: 100%;
}

.message-attachment-item {
  max-width: 100%;
  min-width: 0;
}

.message-image {
  display: block;
  width: auto;
  max-width: min(100%, 300px);
  max-height: 300px;
  height: auto;
  border-radius: 8px;
  cursor: pointer;
}

.message-image-inline {
  margin-bottom: 8px;
}

.file-attachment-bubble {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #F3F4F6;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #4B5563;
  border: 1px solid #E5E5EA;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  max-width: 260px;
  min-width: 0;
  box-sizing: border-box;
}

.file-attachment-bubble:hover {
  background: #E5E7EB;
  border-color: #D1D5DB;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.file-attachment-bubble:active {
  transform: translateY(0);
}

.file-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-icon-small {
  color: #4A65E6;
  display: flex;
  flex-shrink: 0;
}

.file-download-icon {
  color: #8A8A8E;
  display: flex;
  flex-shrink: 0;
  margin-left: 4px;
}

.file-attachment-bubble:hover .file-download-icon {
  color: #4A65E6;
}

/* KB Source */
.kb-sources-wrapper {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #E5E5EA;
  max-width: 100%;
}

.kb-sources-header-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  cursor: pointer;
  list-style: none;
}

.kb-sources-header-row::-webkit-details-marker {
  display: none;
}

.kb-sources-wrapper[open] .kb-sources-header-row {
  margin-bottom: 12px;
}

.kb-sources-title {
  font-size: 12px;
  font-weight: 600;
  color: #6B7280;
}

.kb-sources-divider {
  color: #D1D5DB;
  font-size: 12px;
}

.kb-sources-quote-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: #1A3BFF;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
  font-family: inherit;
}

.kb-sources-quote-btn:hover {
  background-color: rgba(26, 59, 255, 0.05);
}

.kb-quote-icon {
  width: 12px;
  height: 12px;
}

.kb-sources-toggle-icon {
  width: 13px;
  height: 13px;
  color: #9CA3AF;
  transition: transform 0.2s ease;
}

.kb-sources-wrapper[open] .kb-sources-toggle-icon {
  transform: rotate(180deg);
}

.kb-sources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
  max-width: 100%;
}

.kb-source-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background-color: #F9FAFB;
  border: 1px solid #E5E5EA;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.kb-source-card:hover {
  border-color: rgba(26, 59, 255, 0.3);
}

.kb-source-card-icon-box {
  width: 22px;
  height: 22px;
  background-color: #9CA3AF;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #FFFFFF;
}

.kb-source-card-icon {
  width: 14px;
  height: 14px;
}

.kb-source-card-text {
  font-size: 13px;
  color: #4B5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

/* Search Suggestion */
.search-suggestion-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 8px 14px;
  border-radius: 12px;
  margin-top: 14px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  box-sizing: border-box;
}

.message-content :deep(.markdown-body),
.message-content :deep(.markdown-body > *),
.message-content :deep(.code-block-wrapper),
.message-content :deep(pre),
.message-content :deep(table) {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.message-content :deep(pre),
.message-content :deep(table),
.message-content :deep(.code-block-wrapper) {
  overflow-x: auto;
}

.message-content :deep(img),
.message-content :deep(video),
.message-content :deep(canvas),
.message-content :deep(svg) {
  max-width: 100%;
  height: auto;
}

.search-suggestion-badge:hover {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.search-suggestion-badge:active {
  transform: translateY(0);
  background: rgba(255, 255, 255, 0.1);
}

.search-suggestion-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1D44FF;
  background: #FFFFFF;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.search-suggestion-text {
  font-size: 13.5px;
  font-weight: 500;
  color: #FFFFFF;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.95;
}

.search-suggestion-arrow {
  color: #FFFFFF;
  opacity: 0.7;
  transition: all 0.25s ease;
  flex-shrink: 0;
}

.search-suggestion-badge:hover .search-suggestion-arrow {
  transform: translateX(3px);
  opacity: 1;
}

/* --- Dark Mode Styles --- */
[data-theme="dark"] .message-content {
  background: var(--bg-message-assistant, #1C1C1E);
  color: var(--text-primary, #FFFFFF);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

[data-theme="dark"] .message-content.generating-border::after {
  background: var(--bg-message-assistant, #1C1C1E);
}

[data-theme="dark"] .message-row.user .message-content {
  background: var(--bg-message-user, #0A84FF);
  color: #FFFFFF;
}

[data-theme="dark"] .trace-id-chip {
  background: transparent;
  color: #8A8A8E;
  border-color: transparent;
}

.workspace-artifacts { margin-top: 12px; border-top: 1px solid rgba(128, 128, 128, .18); }
.workspace-artifact-row { min-height: 58px; padding: 10px 0; display: grid; grid-template-columns: 38px minmax(0, 1fr) auto auto; align-items: center; gap: 9px; border-bottom: 1px solid rgba(128, 128, 128, .14); }
.workspace-artifact-icon { width: 38px; height: 38px; display: grid; place-items: center; border: 1px solid rgba(37, 99, 235, .28); color: #2563eb; font-size: 9px; font-weight: 700; }
.workspace-artifact-info { min-width: 0; display: flex; flex-direction: column; }
.workspace-artifact-info strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; }
.workspace-artifact-info span { margin-top: 3px; color: #6b7280; font-size: 10px; }
.workspace-artifact-row button { border: 1px solid rgba(128, 128, 128, .25); background: transparent; color: inherit; padding: 5px 7px; font-size: 11px; cursor: pointer; }

[data-theme="dark"] .ai-generated-text {
  color: #8A8A8E;
}

[data-theme="dark"] .trace-id-chip:hover {
  background: transparent;
  opacity: 1;
}

[data-theme="dark"] .report-trace-text {
  background: transparent;
  color: #FCA5A5;
  opacity: 0.78;
}

[data-theme="dark"] .report-trace-text:hover,
[data-theme="dark"] .report-trace-text:focus {
  color: #FECACA;
  opacity: 1;
}

[data-theme="dark"] .report-dialog-overlay {
  background: rgba(2, 6, 23, 0.68);
}

[data-theme="dark"] .report-dialog {
  background: #111827;
  border-color: rgba(55, 65, 81, 0.95);
  box-shadow: 0 28px 72px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .report-dialog-header {
  border-bottom-color: #1F2937;
}

[data-theme="dark"] .report-dialog-title-group h3 {
  color: #F9FAFB;
}

[data-theme="dark"] .report-dialog-title-group p,
[data-theme="dark"] .report-field-label {
  color: #9CA3AF;
}

[data-theme="dark"] .report-dialog-close {
  color: #6B7280;
}

[data-theme="dark"] .report-dialog-close:hover:not(:disabled) {
  background: #1F2937;
  color: #F9FAFB;
}

[data-theme="dark"] .report-type-option {
  background: #111827;
  border-color: #374151;
  color: #D1D5DB;
}

[data-theme="dark"] .report-type-option:hover {
  border-color: rgba(248, 113, 113, 0.32);
  color: #FCA5A5;
}

[data-theme="dark"] .report-type-option.active {
  background: rgba(248, 113, 113, 0.14);
  border-color: #F87171;
  color: #FCA5A5;
}

[data-theme="dark"] .report-reason-input {
  background: #0F172A;
  border-color: #374151;
  color: #F9FAFB;
}

[data-theme="dark"] .report-reason-input:focus {
  border-color: #F87171;
  box-shadow: 0 0 0 4px rgba(248, 113, 113, 0.16);
}

[data-theme="dark"] .report-reason-input:disabled {
  background: #111827;
}

[data-theme="dark"] .report-dialog-btn.secondary {
  background: #111827;
  border-color: #374151;
  color: #D1D5DB;
}

[data-theme="dark"] .report-dialog-btn.secondary:hover:not(:disabled) {
  background: #1F2937;
  border-color: #4B5563;
}

[data-theme="dark"] .report-form-error {
  color: #FCA5A5;
}

[data-theme="dark"] .select-text-message {
  border-color: rgba(110, 136, 249, 0.24);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.24);
}

[data-theme="dark"] .select-text-message-body {
  color: #E5E5EA;
}

[data-theme="dark"] .select-text-fill-btn {
  background: rgba(110, 136, 249, 0.16);
  color: #9AA9FF;
}

[data-theme="dark"] .select-text-fill-btn:hover {
  background: #6E88F9;
  color: #FFFFFF;
}

[data-theme="dark"] .select-text-message-name {
  color: #A1A1A5;
}

[data-theme="dark"] .select-text-close-btn {
  color: #8E8E93;
}

[data-theme="dark"] .select-text-close-btn:hover {
  color: #FF6B6B;
  background: rgba(255, 107, 107, 0.12);
}

[data-theme="dark"] .select-text-highlight {
  color: #8C9EFF;
  background: rgba(110, 136, 249, 0.16);
}

[data-theme="dark"] .select-text-action-chip {
  background: rgba(110, 136, 249, 0.14);
  border-color: rgba(110, 136, 249, 0.22);
  color: #9AA9FF;
}

[data-theme="dark"] .select-text-action-chip:hover {
  background: #6E88F9;
  border-color: #6E88F9;
  color: #FFFFFF;
}

[data-theme="dark"] .thinking-block {
  background: #1C1C1E;
  border-color: #2C2C2E;
}

[data-theme="dark"] .workspace-step {
  background: #1C1C1E;
  border-color: #2C2C2E;
}

[data-theme="dark"] .workspace-step-running {
  background: rgba(37, 99, 235, .14);
  border-color: rgba(96, 165, 250, .42);
}

[data-theme="dark"] .workspace-step-error {
  background: rgba(220, 38, 38, .12);
  border-color: rgba(248, 113, 113, .42);
}

[data-theme="dark"] .workspace-step-body strong {
  color: #E5E7EB;
}

[data-theme="dark"] .workspace-step-body small,
[data-theme="dark"] .workspace-step-body em {
  color: #A1A1A5;
}

[data-theme="dark"] .workspace-step-detail {
  border-top-color: #2C2C2E;
}

[data-theme="dark"] .workspace-step-detail-section pre {
  background: rgba(255, 255, 255, .07);
  color: #D1D5DB;
}

[data-theme="dark"] .workspace-step-detail-section code {
  background: rgba(255, 255, 255, .07);
  color: #D1D5DB;
}

[data-theme="dark"] .thinking-title,
[data-theme="dark"] .thinking-content {
  color: #A1A1A5;
  border-top-color: #2C2C2E;
}

[data-theme="dark"] .thinking-block.is-thinking {
  border-color: rgba(74, 101, 230, 0.5);
  box-shadow: 0 0 0 2px rgba(74, 101, 230, 0.2);
}

[data-theme="dark"] .image-skeleton-loader {
  background: #1C1C1E;
  border-color: #2C2C2E;
}

[data-theme="dark"] .skeleton-image {
  background: #2C2C2E;
}

[data-theme="dark"] .export-menu {
  background: #1C1C1E;
  border-color: #2C2C2E;
}

[data-theme="dark"] .export-item {
  color: #A1A1A5;
}

[data-theme="dark"] .export-item:hover {
  background: #2C2C2E;
  color: #FFFFFF;
}

[data-theme="dark"] .file-attachment-bubble {
  background: #1C1C1E;
  border-color: #2C2C2E;
  color: #A1A1A5;
}

[data-theme="dark"] .action-btn {
  color: #A1A1A5;
}

[data-theme="dark"] .action-btn:hover {
  background: #2C2C2E;
  color: #FFFFFF;
}

[data-theme="dark"] .kb-source-card {
  background-color: #1F2937;
  border-color: #374151;
}

[data-theme="dark"] .kb-source-card:hover {
  border-color: rgba(96, 165, 250, 0.3);
}

[data-theme="dark"] .kb-source-card-text {
  color: #D1D5DB;
}

[data-theme="dark"] .kb-sources-title {
  color: #9CA3AF;
}

[data-theme="dark"] .kb-sources-divider {
  color: #374151;
}

[data-theme="dark"] .kb-sources-quote-btn {
  color: #60A5FA;
}

[data-theme="dark"] .search-suggestion-badge {
  background: linear-gradient(135deg, rgba(74, 101, 230, 0.15), rgba(110, 136, 249, 0.05));
  border-color: rgba(74, 101, 230, 0.3);
}

[data-theme="dark"] .search-suggestion-badge:hover {
  background: linear-gradient(135deg, rgba(74, 101, 230, 0.25), rgba(110, 136, 249, 0.1));
  border-color: rgba(74, 101, 230, 0.5);
  box-shadow: 0 4px 12px rgba(74, 101, 230, 0.2);
}

[data-theme="dark"] .search-suggestion-icon {
  background: #1C1C1E;
  color: #8A9CFF;
}

[data-theme="dark"] .search-suggestion-text {
  color: #8A9CFF;
}

[data-theme="dark"] .search-suggestion-arrow {
  color: #8A9CFF;
}

[data-theme="dark"] .kb-sources-wrapper {
  border-top-color: #2C2C2E;
}

/* Command Confirmation Embedded */
.command-embed-container {
  margin-top: 14px;
  background: #111111;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  font-size: 0.9rem;
  max-width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.command-embed-container:hover {
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.command-embed-header {
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: #e5e7eb;
  letter-spacing: 0.5px;
}

.command-icon {
  color: #fbbf24;
  filter: drop-shadow(0 0 4px rgba(251, 191, 36, 0.4));
  font-size: 1.1rem;
}

.command-embed-content {
  padding: 12px 14px;
  background: #000000;
}

.command-embed-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'Fira Code', 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  color: #10b981; /* Matrix green or #93c5fd for blue */
  line-height: 1.5;
}

.command-embed-actions {
  padding: 10px 14px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(255, 255, 255, 0.02);
}

.embed-btn {
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
}

.embed-btn.cancel {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #9ca3af;
}

.embed-btn.cancel:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #f3f4f6;
  border-color: rgba(255, 255, 255, 0.2);
}

.embed-btn.confirm {
  background: #ffffff;
  color: #000000;
  border-color: #ffffff;
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

.embed-btn.confirm:hover {
  background: #f3f4f6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.15);
}

.embed-btn.confirm:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .message-wrapper,
  .select-text-message-wrapper {
    max-width: 100%;
  }

  .message-content {
    padding: 12px 14px;
  }

  .kb-sources-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
